import { useState, useCallback, useEffect, useMemo } from 'react';
import { Alert, Collapse, CircularProgress } from '@mui/material';
import { Add as AddIcon, Remove as RemoveIcon } from '@mui/icons-material';
import {
  GlobalButton,
  AutocompleteInput,
  TextInput,
  DateInput,
  TimeInput,
  SelectMultiInput,
} from '@/components';
import { GlobalButtonVariant } from '@/components/buttons/GlobalButton';
import {
  Search as SearchIcon,
  DeleteForever as TrashIcon,
  FileCopy as ExportIcon,
} from '@mui/icons-material';
import { Option } from '@/components/inputs/AutocompleteInput';
import {
  useLogisticOperatorsAutocomplete,
  useCommercesAutocomplete,
  useLogisticOperatorServicesOptions,
  useShippingStatusesAutocomplete,
  useDestinationStateOrDepartmentsAutocomplete,
} from '../hooks';
import useDriversAutocomplete from '../../Drivers/hooks/useDriversAutocomplete';
import {
  RecipientAutocompleteInput,
  DestinationAddressAutocompleteInput,
  OriginAddressAutocompleteInput,
} from './searchAutocomplete';
import { useCitiesOptions } from '@/pages/Business/components/geographic/Cities/hooks';
import { ShippingsFilter } from '../models';
import type { LogisticOperator } from '../../LogisticOperator/models';
import { useGetCommerceByIdQuery } from '@/pages/Business/pages/Commerce/services/commerces.service';
import { useGetLogisticOperatorByIdQuery } from '@/pages/Business/pages/LogisticOperator/services';
import { useGetShippingStatusByIdQuery } from '@/pages/Business/pages/LogisticOperator/services/shipping-statuses.service';
import {
  applyTimeToCalendarDate,
  mergeCalendarDateKeepingTime,
  startOfLocalDay,
  endOfLocalDay,
} from '@/utils/dates';
import { useGetZoneByIdQuery } from '@/pages/Business/pages/LogisticOperator/services/zones.service';
import { resolveSelectedOptionLabel } from '@/utils/selected-option-label';
import useZonesAutocomplete from '../hooks/useZonesAutocomplete';
import { useShippingsExport } from '../hooks/useShippingsExport';

interface SearchBarProps {
  initialFilter: ShippingsFilter;
  filter: ShippingsFilter;
  search: (updatedFilter: ShippingsFilter) => void;
  isLoading?: boolean;
}

function getOptionId(option: Option | null): string {
  const data = option?.data;
  if (!data) return '';
  if (typeof data === 'string') return data;
  if (typeof data === 'object' && 'id' in data) {
    return (data as { id: string }).id ?? '';
  }
  return '';
}

const SearchBar = ({
  initialFilter,
  filter,
  search,
  isLoading = false,
}: SearchBarProps) => {
  const [localFilter, setLocalFilter] = useState<ShippingsFilter>(filter);
  const [logisticOperatorInput, setLogisticOperatorInput] = useState('');
  const [commerceInput, setCommerceInput] = useState('');
  const [driverInput, setDriverInput] = useState('');
  const [driverSelection, setDriverSelection] = useState<Option | null>(null);
  const [cityInput, setCityInput] = useState('');
  const [shippingStatusInput, setShippingStatusInput] = useState('');
  const [destinationDepartmentInput, setDestinationDepartmentInput] =
    useState('');
  const [destinationDepartmentSelection, setDestinationDepartmentSelection] =
    useState<Option | null>(null);
  const [isAdditionalFiltersOpen, setIsAdditionalFiltersOpen] = useState(() => {
    return localStorage.getItem('shippings_isAdditionalFiltersOpen') === 'true';
  });
  const [zoneInput, setZoneInput] = useState('');
  const [zoneSelection, setZoneSelection] = useState<Option | null>(null);

  const logisticOperatorId = localFilter.logisticOperatorId?.trim() ?? '';
  const commerceIdForQuery = localFilter.commerceId?.trim() ?? '';
  const cityIdForDisplay =
    localFilter.cityIds?.length === 1 ? localFilter.cityIds[0].trim() : '';
  const destinationStateOrDepartmentIdForDisplay =
    localFilter.destinationStateOrDepartmentIds?.length === 1
      ? localFilter.destinationStateOrDepartmentIds[0].trim()
      : '';
  const cityNameForDisplay =
    localFilter.cityNames?.length === 1 ? localFilter.cityNames[0].trim() : '';
  const destinationStateOrDepartmentNameForDisplay =
    localFilter.destinationStateOrDepartmentName?.trim() ?? '';
  const zoneIdForDisplay =
    localFilter.zoneIds?.length === 1 ? localFilter.zoneIds[0].trim() : '';
  const zoneNameForDisplay =
    localFilter.zoneNames?.length === 1 ? localFilter.zoneNames[0].trim() : '';
  const statusIdForQuery = localFilter.statusId?.trim() ?? '';

  const {
    data: logisticOperatorDetails,
    isFetching: isLogisticOperatorDetailsFetching,
  } = useGetLogisticOperatorByIdQuery(
    { logisticOperatorId: logisticOperatorId },
    { skip: !logisticOperatorId }
  );
  const { data: commerceDetails, isFetching: isCommerceDetailsFetching } =
    useGetCommerceByIdQuery(
    { commerceId: commerceIdForQuery },
    { skip: !commerceIdForQuery }
  );
  const {
    data: shippingStatusDetails,
    isFetching: isShippingStatusDetailsFetching,
  } = useGetShippingStatusByIdQuery(
    { shippingStatusId: statusIdForQuery },
    { skip: !statusIdForQuery }
  );
  const { data: zoneDetails, isFetching: isZoneDetailsFetching } =
    useGetZoneByIdQuery(
    { zoneId: zoneIdForDisplay },
    { skip: !zoneIdForDisplay }
  );

  const selectedLogisticOperator = useMemo((): Option | null => {
    if (!logisticOperatorId) return null;
    if (logisticOperatorDetails) {
      return {
        label: logisticOperatorDetails.fullName,
        data: logisticOperatorDetails,
      };
    }
    return {
      label: resolveSelectedOptionLabel(null, {
        isResolving: isLogisticOperatorDetailsFetching,
        fallback: 'Operador seleccionado',
      }),
      data: {
        id: logisticOperatorId,
        fullName: '',
        businessName: '',
      } as LogisticOperator,
    };
  }, [
    logisticOperatorId,
    logisticOperatorDetails,
    isLogisticOperatorDetailsFetching,
  ]);

  const selectedCommerce = useMemo((): Option | null => {
    if (!commerceIdForQuery) return null;
    return {
      label: resolveSelectedOptionLabel(commerceDetails?.name, {
        isResolving: isCommerceDetailsFetching,
        fallback: 'Comercio seleccionado',
      }),
      data: commerceIdForQuery,
    };
  }, [commerceIdForQuery, commerceDetails?.name, isCommerceDetailsFetching]);

  const selectedCity = useMemo((): Option | null => {
    if (!cityIdForDisplay) return null;
    return {
      label: resolveSelectedOptionLabel(cityNameForDisplay, {
        isResolving: false,
        fallback: 'Ciudad seleccionada',
      }),
      data: cityIdForDisplay,
    };
  }, [cityIdForDisplay, cityNameForDisplay]);

  const selectedDestinationStateOrDepartment = useMemo((): Option | null => {
    const id = destinationStateOrDepartmentIdForDisplay;
    if (!id) return null;
    if (
      destinationDepartmentSelection &&
      getOptionId(destinationDepartmentSelection) === id
    ) {
      return destinationDepartmentSelection;
    }
    return {
      label: destinationStateOrDepartmentNameForDisplay,
      data: id,
    };
  }, [
    destinationStateOrDepartmentIdForDisplay,
    destinationStateOrDepartmentNameForDisplay,
    destinationDepartmentSelection,
  ]);

  const selectedZone = useMemo((): Option | null => {
    const id = zoneIdForDisplay;
    if (!id) return null;
    if (zoneSelection && getOptionId(zoneSelection) === id) {
      return zoneSelection;
    }
    return {
      label: resolveSelectedOptionLabel(
        zoneDetails?.name || zoneNameForDisplay,
        { isResolving: isZoneDetailsFetching, fallback: 'Zona seleccionada' }
      ),
      data: id,
    };
  }, [
    zoneIdForDisplay,
    zoneNameForDisplay,
    zoneSelection,
    zoneDetails,
    isZoneDetailsFetching,
  ]);

  const selectedShippingStatus = useMemo((): Option | null => {
    if (!statusIdForQuery) return null;
    const displayLabel = resolveSelectedOptionLabel(
      shippingStatusDetails?.commercialName || shippingStatusDetails?.name,
      {
        isResolving: isShippingStatusDetailsFetching,
        fallback: 'Estado seleccionado',
      }
    );
    return { label: displayLabel, data: statusIdForQuery };
  }, [
    statusIdForQuery,
    shippingStatusDetails,
    isShippingStatusDetailsFetching,
  ]);

  useEffect(() => {
    setLocalFilter(filter);
    setLogisticOperatorInput('');
    setCommerceInput('');
    setCityInput('');
    setShippingStatusInput('');
    setDestinationDepartmentInput('');
    const deptId = filter.destinationStateOrDepartmentIds?.[0]?.trim() ?? '';
    setDestinationDepartmentSelection((prev) => {
      if (!deptId) return null;
      if (prev && getOptionId(prev) === deptId) return prev;
      return null;
    });

    setZoneInput('');
    const zoneId = filter.zoneIds?.[0]?.trim() ?? '';
    setZoneSelection((prev) => {
      if (!zoneId) return null;
      if (prev && getOptionId(prev) === zoneId) return prev;
      return null;
    });
  }, [filter]);

  const { logisticOperatorOptions, isLogisticOperatorsLoading } =
    useLogisticOperatorsAutocomplete({
      search: logisticOperatorInput,
      minLength: 3,
    });

  const { commerceOptions, isCommercesLoading } = useCommercesAutocomplete({
    search: commerceInput,
    minLength: 3,
  });

  const { cityOptions, isLoading: isCitiesLoading } = useCitiesOptions({
    search: cityInput,
    searchParamKey: 'name',
    stateOrDepartmentId: destinationStateOrDepartmentIdForDisplay,
  });
  const {
    logisticOperatorServiceOptions,
    isLoading: isLogisticOperatorServicesLoading,
    requiresLogisticOperator,
  } = useLogisticOperatorServicesOptions({
    shouldFetch: true,
    logisticOperatorId: localFilter.logisticOperatorId || '',
  });
  const { shippingStatusOptions, isShippingStatusesLoading } =
    useShippingStatusesAutocomplete({
      search: shippingStatusInput,
      minLength: 3,
      enabled: true,
    });

  const {
    destinationStateOrDepartmentOptions,
    isDestinationStateOrDepartmentsLoading,
  } = useDestinationStateOrDepartmentsAutocomplete({
    search: destinationDepartmentInput,
    minLength: 3,
  });

  const { zoneOptions, isZonesLoading } = useZonesAutocomplete({
    search: zoneInput,
    minLength: 3,
  });

  const { driverOptions, isDriversLoading } = useDriversAutocomplete({
    search: driverInput,
    minLength: 3,
  });

  const selectedDriver = useMemo((): Option | null => {
    if (!localFilter.driverId) return null;
    if (driverSelection && driverSelection.data === localFilter.driverId) return driverSelection;
    return null;
  }, [localFilter.driverId, driverSelection]);

  const handleDriverSelect = useCallback((option: Option | null) => {
    const driverId = typeof option?.data === 'string' ? option.data : '';
    setDriverSelection(option);
    setLocalFilter((prev) => ({ ...prev, driverId }));
  }, []);

  const resetFilter = () => {
    setLocalFilter(initialFilter);
    setLogisticOperatorInput('');
    setCommerceInput('');
    setDriverInput('');
    setDriverSelection(null);
    setCityInput('');
    setShippingStatusInput('');
    setDestinationDepartmentInput('');
    setDestinationDepartmentSelection(null);

    setZoneInput('');
    setZoneSelection(null);
    setIsAdditionalFiltersOpen(false);
    localStorage.setItem('shippings_isAdditionalFiltersOpen', 'false');
    search(initialFilter);
  };

  const handleSearch = () => {
    search(localFilter);
  };

  const hasFilterChanges = useMemo(() => {
    const normalize = (value: ShippingsFilter) => {
      const keys = Object.keys(value as Record<string, unknown>).sort();
      return JSON.stringify(value, keys);
    };
    return normalize(localFilter) !== normalize(filter);
  }, [localFilter, filter]);

  const { handleExport, isExporting, getExportDateRangeValidationMessage } =
    useShippingsExport();

  const exportDateRangeValidationMessage =
    getExportDateRangeValidationMessage(localFilter);

  const handleUpdateFilter = useCallback(
    (updatedFilter: Partial<ShippingsFilter>) => {
      setLocalFilter((prev) => ({ ...prev, ...updatedFilter }));
    },
    []
  );

  const handleLogisticOperatorSelect = useCallback((option: Option | null) => {
    const id = getOptionId(option);
    setLocalFilter((prev) => ({
      ...prev,
      logisticOperatorId: id,
      serviceWithOperatorIds: [],
      timeSlotIds: [],
    }));
  }, []);

  const handleCommerceSelect = useCallback((option: Option | null) => {
    const commerceId =
      typeof option?.data === 'string' ? option.data : getOptionId(option);
    setLocalFilter((prev) => ({
      ...prev,
      commerceId,
    }));
  }, []);

  const handleCitySelect = useCallback((option: Option | null) => {
    setLocalFilter((prev) => ({
      ...prev,
      cityIds: option ? [String(option.data)] : [],
      cityNames: option ? [option.label] : [],
    }));
  }, []);

  const handleShippingStatusSelect = useCallback((option: Option | null) => {
    if (!option) {
      setLocalFilter((prev) => ({ ...prev, statusId: '' }));
      return;
    }
    if (typeof option.data !== 'string') return;
    const statusId = option.data;
    setLocalFilter((prev) => ({ ...prev, statusId }));
  }, []);

  const handleDestinationStateOrDepartmentSelect = useCallback(
    (option: Option | null) => {
      setDestinationDepartmentSelection(option);
      setLocalFilter((prev) => ({
        ...prev,
        destinationStateOrDepartmentIds: option ? [getOptionId(option)] : [],
        destinationStateOrDepartmentName: option?.label ?? '',
        cityIds: [],
        cityNames: [],
      }));
    },
    []
  );

  const handleZoneSelect = useCallback((option: Option | null) => {
    setZoneSelection(option);
    setLocalFilter((prev) => ({
      ...prev,
      zoneIds: option ? [getOptionId(option)] : [],
      zoneNames: option?.label ? [option.label] : [],
    }));
  }, []);

  return (
    <div className='w-full p-5 flex flex-col justify-center bg-white rounded-sm shadow-xl gap-5'>
      <div className='grid grid-cols-4 gap-4'>
        <div className='bg-gray-50 p-4 rounded-lg'>
          <h3 className='text-lg font-semibold text-gray-700 mb-4'>
            Filtros de Búsqueda
          </h3>
          <div className='flex flex-col gap-4'>
            <AutocompleteInput
              name='logistic-operator-shipping-search'
              placeholder='Operador logístico'
              value={selectedLogisticOperator}
              inputValue={
                selectedLogisticOperator
                  ? selectedLogisticOperator.label
                  : logisticOperatorInput
              }
              onChange={handleLogisticOperatorSelect}
              onInputChange={setLogisticOperatorInput}
              options={logisticOperatorOptions}
              required={false}
              loading={isLogisticOperatorsLoading}
            />
            <AutocompleteInput
              name='commerce-shipping-search'
              placeholder='Comercio'
              value={selectedCommerce}
              inputValue={
                selectedCommerce ? selectedCommerce.label : commerceInput
              }
              onChange={handleCommerceSelect}
              onInputChange={setCommerceInput}
              options={commerceOptions}
              required={false}
              loading={isCommercesLoading}
            />
            <AutocompleteInput
              name='driver-shipping-search'
              placeholder='Conductor (escriba 3+ caracteres para buscar)'
              value={selectedDriver}
              inputValue={selectedDriver ? selectedDriver.label : driverInput}
              onChange={handleDriverSelect}
              onInputChange={setDriverInput}
              options={driverOptions}
              required={false}
              loading={isDriversLoading}
            />
          </div>
        </div>

        <div className='bg-gray-50 p-4 rounded-lg'>
          <h3 className='text-lg font-semibold text-gray-700 mb-4'>
            Identificadores y Etiquetas
          </h3>
          <div className='flex flex-col gap-4'>
            <TextInput
              name='number-shipping-search'
              placeholder='ID Pedido'
              value={localFilter.number || ''}
              onChange={(value) =>
                handleUpdateFilter({
                  number: value.toString(),
                })
              }
            />
            <TextInput
              name='commerce-shipping-number-search'
              placeholder='ID Externo'
              value={localFilter.commerceShippingNumber || ''}
              onChange={(value) =>
                handleUpdateFilter({
                  commerceShippingNumber: value.toString(),
                })
              }
            />
            <TextInput
              name='label-number-search'
              placeholder='N° de etiqueta del correo'
              value={localFilter.labelNumber || ''}
              onChange={(value) =>
                handleUpdateFilter({
                  labelNumber: value.toString(),
                })
              }
            />
            <TextInput
              name='tags-search'
              placeholder='Etiquetas'
              value={localFilter.tags || ''}
              onChange={(value) =>
                handleUpdateFilter({
                  tags: value.toString(),
                })
              }
            />
          </div>
        </div>

        <div className='bg-gray-50 p-4 rounded-lg'>
          <h3 className='text-lg font-semibold text-gray-700 mb-4'>
            Estado y Servicios
          </h3>
          <div className='flex flex-col gap-3.5'>
            <AutocompleteInput
              name='status-search'
              placeholder='Estado'
              value={selectedShippingStatus}
              inputValue={
                selectedShippingStatus
                  ? selectedShippingStatus.label
                  : shippingStatusInput
              }
              onChange={handleShippingStatusSelect}
              onInputChange={setShippingStatusInput}
              options={shippingStatusOptions}
              required={false}
              loading={isShippingStatusesLoading}
            />
            <SelectMultiInput
              name='logistic-operator-service-search'
              placeholder={
                requiresLogisticOperator
                  ? 'Seleccione un operador logístico'
                  : 'Servicios del operador'
              }
              value={localFilter.serviceWithOperatorIds || []}
              onChange={(value) =>
                handleUpdateFilter({
                  serviceWithOperatorIds: value,
                })
              }
              options={logisticOperatorServiceOptions}
              disabled={
                requiresLogisticOperator || isLogisticOperatorServicesLoading
              }
            />
            <SelectMultiInput
              name='shipping-level-search'
              placeholder='Etapa logística'
              value={localFilter.shippingLevels || []}
              onChange={(value) =>
                handleUpdateFilter({ shippingLevels: value })
              }
              options={[
                { label: 'Ingresado', value: 'CREATED' },
                { label: 'Procesado', value: 'WAS_PROCESSED' },
                { label: 'Intento de entrega', value: 'HAS_AN_ATTEMPT' },
                { label: 'Entregado', value: 'IS_DELIVERED' },
              ]}
            />
          </div>
        </div>

        <div className='bg-gray-50 p-4 rounded-lg'>
          <div className='flex flex-col gap-4'>
            <div className='flex flex-col gap-3'>
              <h4 className='text-sm font-medium text-gray-600'>
                Fecha ETA
              </h4>
              <div className='grid grid-cols-2 gap-3'>
                <DateInput
                  name='delivery-date-from'
                  placeholder='Desde'
                  value={localFilter.deliveryDateFrom || null}
                  clearable
                  onChange={(date) =>
                    handleUpdateFilter({ deliveryDateFrom: startOfLocalDay(date) })
                  }
                />
                <DateInput
                  name='delivery-date-to'
                  placeholder='Hasta'
                  value={localFilter.deliveryDateTo || null}
                  clearable
                  onChange={(date) =>
                    handleUpdateFilter({ deliveryDateTo: endOfLocalDay(date) })
                  }
                />
              </div>
            </div>
            <div className='flex flex-col gap-3'>
              <h4 className='text-sm font-medium text-gray-600'>
                Fecha entregado
              </h4>
              <div className='grid grid-cols-2 gap-3'>
                <DateInput
                  name='date-delivered-from'
                  placeholder='Desde'
                  value={localFilter.dateDeliveredFrom || null}
                  clearable
                  onChange={(date) =>
                    handleUpdateFilter({ dateDeliveredFrom: startOfLocalDay(date) })
                  }
                />
                <DateInput
                  name='date-delivered-to'
                  placeholder='Hasta'
                  value={localFilter.dateDeliveredTo || null}
                  clearable
                  onChange={(date) =>
                    handleUpdateFilter({ dateDeliveredTo: endOfLocalDay(date) })
                  }
                />
              </div>
            </div>
            <div className='flex flex-col gap-3'>
              <h4 className='text-sm font-medium text-gray-600'>
                Fecha de ingreso
              </h4>
              <div className='grid grid-cols-2 gap-3'>
                <DateInput
                  name='created-at-from'
                  placeholder='Desde (fecha)'
                  value={localFilter.createdAtFrom || null}
                  onChange={(date) =>
                    handleUpdateFilter({
                      createdAtFrom: mergeCalendarDateKeepingTime(
                        date,
                        localFilter.createdAtFrom ?? null,
                        'start'
                      ),
                    })
                  }
                />
                <TimeInput
                  name='created-at-from-time'
                  placeholder='Desde (hora)'
                  value={localFilter.createdAtFrom || null}
                  disabled={!localFilter.createdAtFrom}
                  onChange={(time) =>
                    handleUpdateFilter({
                      createdAtFrom: applyTimeToCalendarDate(
                        localFilter.createdAtFrom ?? null,
                        time,
                        'start'
                      ),
                    })
                  }
                />
              </div>
              <div className='grid grid-cols-2 gap-3'>
                <DateInput
                  name='created-at-to'
                  placeholder='Hasta (fecha)'
                  value={localFilter.createdAtTo || null}
                  onChange={(date) =>
                    handleUpdateFilter({
                      createdAtTo: mergeCalendarDateKeepingTime(
                        date,
                        localFilter.createdAtTo ?? null,
                        'end'
                      ),
                    })
                  }
                />
                <TimeInput
                  name='created-at-to-time'
                  placeholder='Hasta (hora)'
                  value={localFilter.createdAtTo || null}
                  disabled={!localFilter.createdAtTo}
                  onChange={(time) =>
                    handleUpdateFilter({
                      createdAtTo: applyTimeToCalendarDate(
                        localFilter.createdAtTo ?? null,
                        time,
                        'end'
                      ),
                    })
                  }
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <Collapse in={isAdditionalFiltersOpen} timeout={300} unmountOnExit>
        <div className='grid grid-cols-3 gap-4'>
          <div className='bg-gray-50 p-4 rounded-lg'>
            <h3 className='text-lg font-semibold text-gray-700 mb-4'>
              Direcciones
            </h3>
            <div className='flex flex-col gap-4'>
              <DestinationAddressAutocompleteInput
                name='destination-address-search'
                placeholder='Dirección de entrega'
                value={localFilter.destinationAddress}
                onValueChange={(next) =>
                  handleUpdateFilter({ destinationAddress: next })
                }
                suggestionsEnabled={isAdditionalFiltersOpen}
              />
              <OriginAddressAutocompleteInput
                name='origin-address-search'
                placeholder='Dirección de origen'
                value={localFilter.originAddress}
                onValueChange={(next) =>
                  handleUpdateFilter({ originAddress: next })
                }
                suggestionsEnabled={isAdditionalFiltersOpen}
              />
            </div>
          </div>

          <div className='bg-gray-50 p-4 rounded-lg'>
            <h3 className='text-lg font-semibold text-gray-700 mb-4'>
              Cliente
            </h3>
            <div className='flex flex-col gap-4'>
              <RecipientAutocompleteInput
                name='recipient-name-search'
                placeholder='Destinatario'
                value={localFilter.recipient}
                onValueChange={(next) =>
                  handleUpdateFilter({ recipient: next })
                }
              />
              <TextInput
                name='recipient-phone-search'
                placeholder='Teléfono del cliente'
                value={localFilter.recipientPhoneNumber || ''}
                onChange={(value) =>
                  handleUpdateFilter({
                    recipientPhoneNumber: value
                      .toString()
                      .replace(/[^0-9+]/g, ''),
                  })
                }
              />
            </div>
          </div>

          <div className='bg-gray-50 p-4 rounded-lg'>
            <h3 className='text-lg font-semibold text-gray-700 mb-4'>Ciudad</h3>
            <div className='flex flex-col gap-4'>
              <AutocompleteInput
                name='destination-state-or-department-search'
                placeholder='Departamento de destino'
                value={selectedDestinationStateOrDepartment}
                inputValue={
                  selectedDestinationStateOrDepartment
                    ? selectedDestinationStateOrDepartment.label
                    : destinationDepartmentInput
                }
                onChange={handleDestinationStateOrDepartmentSelect}
                onInputChange={setDestinationDepartmentInput}
                options={destinationStateOrDepartmentOptions}
                required={false}
                loading={isDestinationStateOrDepartmentsLoading}
              />
              <AutocompleteInput
                name='city-search'
                placeholder='Ciudad de destino'
                value={selectedCity}
                inputValue={selectedCity ? selectedCity.label : cityInput}
                onChange={handleCitySelect}
                onInputChange={setCityInput}
                options={cityOptions}
                required={false}
                loading={isCitiesLoading}
              />
              <AutocompleteInput
                name='zone-search'
                placeholder='Zona (escriba 3+ caracteres para buscar)'
                value={selectedZone}
                inputValue={selectedZone ? selectedZone.label : zoneInput}
                onChange={handleZoneSelect}
                onInputChange={setZoneInput}
                options={zoneOptions}
                required={false}
                loading={isZonesLoading}
              />
            </div>
          </div>
        </div>
      </Collapse>
      <div className='flex justify-start'>
        <GlobalButton
          variant={GlobalButtonVariant.secondaryButton}
          action={() => {
            setIsAdditionalFiltersOpen((prev) => {
              const next = !prev;
              localStorage.setItem(
                'shippings_isAdditionalFiltersOpen',
                next.toString()
              );
              return next;
            });
          }}
        >
          {isAdditionalFiltersOpen ? <RemoveIcon /> : <AddIcon />}
          {isAdditionalFiltersOpen ? 'Ocultar' : 'Mostrar'} filtros adicionales
        </GlobalButton>
      </div>
      <div className='flex gap-3 justify-end'>
        <GlobalButton
          variant={GlobalButtonVariant.secondaryButton}
          action={resetFilter}
          disabled={isExporting}
        >
          <TrashIcon color='primary' />
          Limpiar
        </GlobalButton>
        <GlobalButton
          variant={GlobalButtonVariant.secondaryButton}
          action={() => handleExport(localFilter)}
          disabled={isExporting || !!exportDateRangeValidationMessage}
        >
          {isExporting ? (
            <CircularProgress size={20} color='inherit' />
          ) : (
            <ExportIcon color='primary' />
          )}
          Exportar
        </GlobalButton>
        <GlobalButton
          variant={GlobalButtonVariant.primaryButton}
          action={handleSearch}
          disabled={isExporting || isLoading}
        >
          {isLoading ? (
            <CircularProgress size={20} color='inherit' />
          ) : (
            <SearchIcon />
          )}
          {isLoading ? 'Buscando...' : 'Buscar'}
        </GlobalButton>
      </div>
      {exportDateRangeValidationMessage ? (
        <Alert severity='warning'>{exportDateRangeValidationMessage}</Alert>
      ) : null}
    </div>
  );
};

export default SearchBar;
